import { AUTH_ROUTES, requestOtpOutput, tokenPairOutput, verifyOtpOutput, } from "@repo/contract/iam";
import { internalError, toApiError } from "./api-error";
function jsonBody(value) {
    return {
        body: JSON.stringify(value),
        headers: { "content-type": "application/json" },
        method: "POST",
    };
}
/**
 * Read a successful body, or throw.
 *
 * The response is **validated**, not cast. A cast makes a changed server shape
 * arrive as `undefined` deep inside a screen; validating turns it into one
 * error at the boundary, which is where the requestId still exists to log.
 */
export async function readJson(response, decoder) {
    if (!response.ok) {
        throw await toApiError(response);
    }
    let body;
    try {
        body = await response.json();
    }
    catch (cause) {
        throw internalError(response.status, cause);
    }
    const parsed = decoder.safeParse(body);
    if (!parsed.success) {
        throw internalError(response.status);
    }
    return parsed.data;
}
/**
 * ── AN UNKNOWN ADDRESS IS INDISTINGUISHABLE FROM A KNOWN ONE. ────────────────
 * The server writes a real verification row with a real expiry either way and
 * answers with the same status, the same body shape and the same timing; only
 * delivery differs. There is therefore nothing to detect here and nothing for a
 * screen to branch on. The UI must never say "no account for that address" — it
 * does not know, and the honest message for an un-received code is identical in
 * both cases.
 */
export function requestOtp(fetcher, identifier, 
/**
 * The language to write the email in — `RequestOtpInput["locale"]`, so the
 * union is never restated on this side of the wire.
 *
 * Optional here as well as on the contract, and for the same reason: the
 * server treats absent as en-US. That keeps this function callable without a
 * locale in a test, and keeps the shape honest about what an older client
 * actually sends.
 */
locale) {
    return fetcher(AUTH_ROUTES.requestOtp, 
    // The key is omitted rather than sent as `null` when there is no locale:
    // the field is `.optional()`, not `.nullable()`, so an explicit null is a
    // 422 on the sign-in screen.
    jsonBody(locale === undefined
        ? { channel: "email", identifier }
        : { channel: "email", identifier, locale })).then((response) => readJson(response, requestOtpOutput));
}
/**
 * `ref` is the share link this device arrived through (`D260`), sent only
 * when there is one — omitted, never `null`, for the reason `requestOtp`
 * gives above.
 */
export function verifyOtp(fetcher, verificationId, code, ref) {
    return fetcher(AUTH_ROUTES.verifyOtp, jsonBody(ref === undefined
        ? { code, verificationId }
        : { code, ref, verificationId })).then((response) => readJson(response, verifyOtpOutput));
}
/**
 * ── THE REFRESH TOKEN GOES IN THE BODY, NEVER IN `Authorization`. ────────────
 * That header carries the ACCESS token on every other route. A route accepting
 * either credential in the same slot is eventually handed the wrong one, and
 * the failure mode is family revocation: the user signed out of every device
 * because of an unrelated bug.
 *
 * ── AND NOBODY CALLS THIS DIRECTLY. ──────────────────────────────────────────
 * `ApiClient.refresh()` is the only safe entry point, because it is the only
 * thing that guarantees ONE in-flight presentation of a single-use token. Two
 * concurrent calls to this function present the same token twice, the second is
 * reuse, and reuse is treated as theft.
 */
export function refreshTokens(fetcher, refreshToken) {
    return fetcher(AUTH_ROUTES.refresh, jsonBody({ refresh_token: refreshToken })).then((response) => readJson(response, tokenPairOutput));
}
/**
 * Ends the session named by the caller's own access token.
 *
 * The route needs a **valid** access token, so a client whose token has expired
 * must refresh before it can sign out. That is not an oversight in the server:
 * answering 200 to an unauthenticated caller would tell someone they had signed
 * out while leaving their whole refresh family alive.
 */
export async function signout(fetcher, accessToken) {
    const response = await fetcher(AUTH_ROUTES.signout, {
        headers: { authorization: `Bearer ${accessToken}` },
        method: "POST",
    });
    if (!response.ok) {
        throw await toApiError(response);
    }
}
//# sourceMappingURL=auth-api.js.map